import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { CheckCircle, FileText, Film, Download, Loader2, AlertCircle, Clock, Globe } from "lucide-react";
import { getDownloadLinks } from "@/utils/api";
import { getOrCreateAnonUserId } from "@/utils/user";
import { useToast } from '@/hooks/use-toast';
import { buildApiUrl } from '@/config/env';
import { LanguageSelectionModal } from './LanguageSelectionModal';
import { translateSrt } from '@/utils/apiWithRecaptcha';
import { useRecaptcha } from '@/contexts/RecaptchaContext';

interface DownloadSectionProps {
  videoId: string;
  originalFilename: string;
  onSubtitleUpdate?: (newSrtUrl: string) => void; // Callback to update JWPlayer subtitle
}

export default function DownloadSection({ videoId, originalFilename, onSubtitleUpdate }: DownloadSectionProps) {
  const { t } = useTranslation();
  
  // 🚀 CRITICAL DESIGN DECISION: Translation button ALWAYS appears in DownloadSection
  // This button is displayed REGARDLESS of whether user chose translation in step 1
  // User can always translate subtitles to another language at any time
  // This provides flexibility and better user experience
  
  const [downloading, setDownloading] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState<string>('checking');
  const [previewProgress, setPreviewProgress] = useState<number>(0);
  const [fullProgress, setFullProgress] = useState<number>(0);
  const [isFullVideoReady, setIsFullVideoReady] = useState<boolean>(false);
  
  // 🚀 NEW: Translation state
  const [showLanguageModal, setShowLanguageModal] = useState<boolean>(false);
  const [isTranslating, setIsTranslating] = useState<boolean>(false);
  const [translatedSrtUrl, setTranslatedSrtUrl] = useState<string | null>(null);
  const [selectedTargetLanguage, setSelectedTargetLanguage] = useState<string>('0');
  const [selectedSourceLanguage, setSelectedSourceLanguage] = useState<string>('auto');
  
  const { executeRecaptcha } = useRecaptcha();
  const { toast } = useToast();

  // Check if full video processing is complete
  useEffect(() => {
    // Use a ref to track if monitoring is active
    const monitoringRef = { current: true };
    
    const checkProcessingStatus = async () => {
      // Stop if monitoring was cancelled
      if (!monitoringRef.current) {
        return;
      }
      
      try {
        // 🚀 Fix: Call Cloudflare Worker directly instead of localhost
        const response = await fetch(`${buildApiUrl('api/job/status')}?video_id=${videoId}`);
        if (response.ok) {
          const data = await response.json();
          
          if (data.preview_process?.status === 'preview_ready' || data.status === 'completed' || data.full_process?.status === 'completed') {
            setProcessingStatus('completed');
            setIsFullVideoReady(true);
            monitoringRef.current = false; // Stop monitoring
            return;
          } else if (data.preview_process?.status === 'processing') {
            setProcessingStatus('processing');
            setPreviewProgress(data.progress || 0);
            setFullProgress(0);
            // Continue checking
            setTimeout(checkProcessingStatus, 3000);
          } else if (data.full_process?.status === 'full_processing') {
            setProcessingStatus('processing');
            setPreviewProgress(100); // Preview is done
            setFullProgress(data.full_progress || 0);
            // Continue checking
            setTimeout(checkProcessingStatus, 3000);
          } else {
            setProcessingStatus('pending');
            // Continue checking
            setTimeout(checkProcessingStatus, 5000);
          }
        }
      } catch (error) {
        console.error('Failed to check processing status:', error);
        setProcessingStatus('error');
      }
    };

    checkProcessingStatus();
    
    // Cleanup function to stop monitoring when component unmounts or videoId changes
    return () => {
      monitoringRef.current = false;
    };
  }, [videoId]);

  const downloadFile = async (format: string) => {
    if (!isFullVideoReady) {
      return;
    }

    try {
      setDownloading(format);
      const response = await getDownloadLinks(
        videoId,
        format as 'srt' | 'video'
      );
      
      // Open in new tab for all devices to avoid memory issues with large files
      const link = document.createElement('a');
      link.href = response.download_url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      // Show user-friendly error message
      alert('Download failed. Please try again later.');
    } finally {
      setDownloading(null);
    }
  };

  const downloadAll = async () => {
    if (!isFullVideoReady) {
      return;
    }

    // Download all formats
    const formats = ['srt', 'video'];
    for (const format of formats) {
      await downloadFile(format);
      // Small delay between downloads
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  // 🚀 NEW: Translation functions
  const handleLanguageModalConfirm = async (selectedLanguage: string | null) => {
    if (!selectedLanguage || selectedLanguage === '0') {
      setShowLanguageModal(false);
      return;
    }

    setSelectedTargetLanguage(selectedLanguage);
    setShowLanguageModal(false);
    
    // Start translation immediately
    await startTranslation();
  };

  const startTranslation = async () => {
    if (!selectedTargetLanguage || selectedTargetLanguage === '0') {
      toast({
        title: "Language not selected",
        description: "Please select a target language for translation.",
        variant: "destructive",
      });
      return;
    }

    setIsTranslating(true);
    try {
      // Get the full SRT URL from the current video data
      const response = await fetch(`${buildApiUrl('api/job/status')}?video_id=${videoId}`);
      if (!response.ok) {
        throw new Error('Failed to get video data');
      }

      const data = await response.json();
      const fullSrtUrl = data.full?.s3_srt_url;
      
      if (!fullSrtUrl) {
        throw new Error('Full video SRT not available');
      }

      console.log('🌐 Starting translation for full video:', {
        srtUrl: fullSrtUrl,
        targetLanguage: selectedTargetLanguage,
        sourceLanguage: selectedSourceLanguage
      });

      const result = await translateSrt(
        fullSrtUrl,
        selectedTargetLanguage,
        selectedSourceLanguage === 'auto' ? 'auto' : selectedSourceLanguage,
        videoId,
        executeRecaptcha
      );

      console.log('✅ Translation completed:', result);
      setTranslatedSrtUrl(result.translated_srt_url);
      
      // Update JWPlayer subtitle if callback is provided
      if (onSubtitleUpdate) {
        onSubtitleUpdate(result.translated_srt_url);
      }

      toast({
        title: "Translation completed!",
        description: `Subtitles translated to ${selectedTargetLanguage} with context-aware technology for natural movie dialogue`,
        variant: "default",
      });

    } catch (error) {
      console.error('❌ Translation error:', error);
      toast({
        title: "Translation failed",
        description: error instanceof Error ? error.message : "Failed to translate subtitles",
        variant: "destructive",
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const openLanguageModal = () => {
    console.log('🌐 DownloadSection - Opening language modal for translation');
    setShowLanguageModal(true);
  };

  // Show processing status if not ready
  if (!isFullVideoReady) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {processingStatus === 'processing' ? (
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            ) : processingStatus === 'error' ? (
              <AlertCircle className="h-8 w-8 text-red-600" />
            ) : (
              <Clock className="h-8 w-8 text-blue-600" />
            )}
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {processingStatus === 'processing' ? 'Processing Full Video' : 'Preparing Download'}
          </h3>
          <p className="text-gray-600 mb-4">
            {processingStatus === 'processing' 
              ? `Full video processing in progress... ${fullProgress}%`
              : 'Please wait while we prepare your files for download.'
            }
          </p>
          {processingStatus === 'processing' && (
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
                style={{ width: `${fullProgress}%` }}
              ></div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">
          {t('download.title')}
        </h3>
        <p className="text-gray-600">
          {t('download.description')}
        </p>
      </div>

      {/* Download Options */}
      {/* 🚀 IMPORTANT: Translation button ALWAYS appears here, regardless of step 1 translation choice */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Subtitle Files */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            <FileText className="inline h-5 w-4 text-blue-500 mr-2" />
            {t('download.subtitleFiles')}
          </h4>

          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-between"
              onClick={() => downloadFile('srt')}
              disabled={downloading === 'srt'}
            >
              <div className="flex items-center">
                <FileText className="h-4 w-4 text-gray-400 mr-3" />
                <div className="text-left">
                  <div className="font-medium text-gray-900">{originalFilename}.srt</div>
                  <div className="text-sm text-gray-500">SubRip format • 3.2 KB</div>
                </div>
              </div>
              {downloading === 'srt' ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              ) : (
                <Download className="h-4 w-4 text-blue-500" />
              )}
            </Button>

            {/* 🚀 CRITICAL: Translation button ALWAYS appears here */}
            {/* This button is displayed REGARDLESS of whether user chose translation in step 1 */}
            {/* User can always translate subtitles to another language at any time */}
            <Button
              variant="outline"
              className="w-full justify-between border-green-200 bg-green-50 hover:bg-green-100"
              onClick={openLanguageModal}
              disabled={isTranslating}
            >
              <div className="flex items-center">
                <Globe className="h-4 w-4 text-green-500 mr-3" />
                <div className="text-left">
                  <div className="font-medium text-gray-900">Translate to another language</div>
                  <div className="text-sm text-gray-500">
                    {isTranslating ? 'Translating...' : 'Get subtitles in your preferred language'}
                  </div>
                </div>
              </div>
              {isTranslating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
              ) : (
                <Globe className="h-4 w-4 text-green-500" />
              )}
            </Button>
          </div>
        </div>

        {/* Translated Subtitles - Only show when available */}
        <div className="border border-green-200 rounded-lg p-6 bg-green-50">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            <FileText className="inline h-5 w-5 text-green-500 mr-2" />
            Translated Subtitles
          </h4>

          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-between"
              onClick={() => downloadFile('translated_srt')}
              disabled={downloading === 'translated_srt'}
            >
              <div className="flex items-center">
                <FileText className="h-4 w-4 text-gray-400 mr-3" />
                <div className="text-left">
                  <div className="font-medium text-gray-900">{originalFilename}_translated.srt</div>
                  <div className="text-sm text-gray-500">Translated subtitles (SRT)</div>
                </div>
              </div>
              {downloading === 'translated_srt' ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              ) : (
                <Download className="h-4 w-4 text-green-500" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Download All Button */}
      <div className="mt-6">
        <Button
          onClick={downloadAll}
          className="w-full"
          disabled={downloading !== null}
        >
          {downloading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Downloading...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              {t('download.downloadAll')}
            </>
          )}
        </Button>
      </div>

      {/* Auto-delete warning */}
      <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center">
          <AlertCircle className="h-4 w-4 text-yellow-600 mr-2" />
          <span className="text-sm text-yellow-800">
            {t('download.autoDeleteWarning', { time: 60 })}
          </span>
        </div>
      </div>

      {/* 🚀 NEW: Language Selection Modal */}
      <LanguageSelectionModal
        isOpen={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
        onConfirm={handleLanguageModalConfirm}
        fileName={originalFilename}
      />
    </div>
  );
}
